import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp } from "react-feather";
import {
  FaUserInjured,
  FaHandHoldingHeart,
  FaTint,
  FaIdCard,
} from "react-icons/fa";
import { SuccessBadge, ScoreBar, useMatchScore } from "./MatchSuccessScore";

const ThreeWayCycleDisplay = ({ cycles }) => {
  const [expandedCycle, setExpandedCycle] = useState(null);

  const toggleExpand = (index) => {
    setExpandedCycle(expandedCycle === index ? null : index);
  };

  return (
    <div className="container p-1 mx-auto md:p-4">
      <div className="flex flex-col items-center justify-between mb-4 sm:flex-row">
        <div>
          <h1 className="text-2xl font-bold text-purple-800 sm:text-3xl">
            {cycles.length >= 50
              ? `Top ${cycles.length} Viable 3-Way Cycles`
              : `3-Way Cycles Found: ${cycles.length}`}
          </h1>
          <p className="mt-1 text-xs text-gray-600 sm:text-sm">
            Cycles evaluated and ranked by active donor availability, logistical proximity, and clinical viability.
          </p>
        </div>
        {cycles.length > 0 && (
          <span className="mt-2 sm:mt-0 inline-flex items-center px-3 py-1 text-xs font-semibold text-emerald-800 bg-emerald-100 rounded-full border border-emerald-300">
            ⭐ Rank #1 Best Match Available
          </span>
        )}
      </div>

      <div className="p-4 mb-5 border rounded-xl bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50 border-purple-200 text-sm text-purple-900 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-purple-600 text-white flex items-center justify-center shrink-0 text-base font-bold shadow-sm">
            3W
          </div>
          <div>
            <p className="font-bold text-purple-950">
              Closed-Loop Viable 3-Way Matching Architecture
            </p>
            <p className="mt-0.5 text-xs text-purple-800 leading-relaxed">
              A 3-way cycle resolves three mutually incompatible pairs in a closed ring: <strong>Recipient 1</strong> receives from <strong>Donor 2</strong>, <strong>Recipient 2</strong> receives from <strong>Donor 3</strong>, and <strong>Recipient 3</strong> receives from <strong>Donor 1</strong>. Candidates are prioritized by donor active status, regional transport proximity, and complete clinical documentation to ensure the safest, most actionable match.
            </p>
          </div>
        </div>
      </div>
      {cycles.map((cycle, index) => (
        <CycleCard
          key={index}
          cycle={cycle}
          index={index}
          isExpanded={expandedCycle === index}
          onToggle={() => toggleExpand(index)}
        />
      ))}
    </div>
  );
};

const ThreeWayScores = ({ cycle }) => {
  // Compute scores for all 3 sub-pairs
  const s1 = useMatchScore(cycle.pair1.recipient?._id, cycle.pair2.donor?._id);
  const s2 = useMatchScore(cycle.pair2.recipient?._id, cycle.pair3.donor?._id);
  const s3 = useMatchScore(cycle.pair3.recipient?._id, cycle.pair1.donor?._id);

  const exchangeResults = [s1, s2, s3];
  const scores = exchangeResults.map(r => r.compositeScore);
  const incompatible = s1.isIncompatible || s2.isIncompatible || s3.isIncompatible;
  const loading = s1.loading || s2.loading || s3.loading;

  // Overall = min of all 3 (all must work for the cycle to complete)
  const validScores = scores.filter(s => s !== null);
  const overall = validScores.length === 3
    ? Math.round(Math.min(...validScores))
    : validScores.length > 0
      ? Math.round(Math.min(...validScores))
      : null;

  // Find the weakest link
  const minIdx = overall !== null ? scores.indexOf(Math.min(...validScores)) : -1;

  const pairLabels = ["R1 ← D2", "R2 ← D3", "R3 ← D1"];
  const pairColors = ["bg-blue-50 border-blue-200", "bg-green-50 border-green-200", "bg-orange-50 border-orange-200"];

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full">
        <div className="w-4 h-4 border-2 border-gray-400 border-t-gray-600 rounded-full animate-spin" />
        <span className="text-xs text-gray-500 font-medium">Analyzing cycle compatibility...</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Overall cycle score */}
      <div className="flex items-center gap-3">
        <SuccessBadge score={overall} size="md" isIncompatible={incompatible} />
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-gray-800">
            Cycle Success Score
          </span>
          <span className="text-[10px] text-gray-500">
            {overall !== null && overall >= 80
              ? "All 3 exchanges are strong — cycle is viable"
              : overall !== null && overall >= 60
                ? "Cycle is likely viable — review weak exchanges below"
                : overall !== null
                  ? "One or more exchanges are high risk — consider alternatives"
                  : "Scoring incomplete"}
          </span>
        </div>
      </div>
      {overall !== null && <ScoreBar score={overall} className="w-full max-w-md" isIncompatible={incompatible} />}

      {/* Individual sub-pair scores */}
      <div className="grid grid-cols-1 gap-2 mt-3 md:grid-cols-3">            {scores.map((score, i) => {
              const subIncompatible = exchangeResults[i]?.isIncompatible || false;
              return (
              <div
                key={i}
                className={`p-2 rounded border ${pairColors[i]} ${
                  i === minIdx && overall !== null && overall < 60
                    ? "ring-2 ring-red-300"
                    : ""
                }`}
              >
                <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1">
                  {pairLabels[i]}
                  {i === minIdx && overall !== null && overall < 60 && (
                    <span className="ml-1 text-red-500">⚠️ Weakest</span>
                  )}
                </p>
                <div className="flex items-center gap-1.5">
                  <SuccessBadge score={score} size="sm" showLabel={false} isIncompatible={subIncompatible} />
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-gray-700">
                      {score !== null ? `${Math.round(score)}%` : "—"}
                    </span>
                    <ScoreBar score={score} className="w-12" />
                  </div>
                </div>
              </div>
            );
          })}
          </div>
    </div>
  );
};

const CycleCard = ({ cycle, index, isExpanded, onToggle }) => {
  let navigate = useNavigate();
  const isBestMatch = index === 0;

  return (
    <div
      className={`mb-6 overflow-hidden bg-white rounded-xl transition-all duration-300 ${
        isBestMatch
          ? "border-2 border-emerald-500 shadow-xl ring-2 ring-emerald-200/60"
          : isExpanded
          ? "border-2 border-purple-300 shadow-lg bg-purple-50/20"
          : "border border-gray-200 shadow-md hover:shadow-lg"
      }`}
    >
      {/* Best Match Top Ribbon */}
      {isBestMatch && (
        <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 text-white px-4 py-2 flex flex-wrap items-center justify-between text-xs font-bold tracking-wide uppercase shadow-inner">
          <div className="flex items-center gap-2">
            <span className="text-sm">⭐</span>
            <span>Recommended Best Match (Rank #1)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="bg-white/20 px-2 py-0.5 rounded text-[11px] font-semibold text-emerald-100">
              Viability Score: {cycle.viabilityScore || 175} pts
            </span>
            <span className="bg-emerald-400/30 text-emerald-100 px-2 py-0.5 rounded text-[11px]">
              Optimal Exchange Matrix
            </span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50/80 via-indigo-50/60 to-purple-50/80 border-b border-gray-100">
        <div className="flex items-center gap-2.5 flex-wrap">
          <h2 className="text-lg sm:text-xl font-bold text-purple-900">
            Cycle {cycle.rank || index + 1}
          </h2>

          {/* Viability Tier Badge */}
          {cycle.viabilityTier && (
            <span
              className={`px-2.5 py-0.5 text-xs font-bold rounded-full border ${
                cycle.viabilityTier === "EXCELLENT"
                  ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                  : cycle.viabilityTier === "GOOD"
                  ? "bg-blue-100 text-blue-800 border-blue-300"
                  : "bg-amber-100 text-amber-800 border-amber-300"
              }`}
            >
              Viability: {cycle.viabilityTier} ({cycle.viabilityScore} pts)
            </span>
          )}

          {cycle.allDonorsActive && (
            <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-green-50 text-green-700 border border-green-200 flex items-center gap-1">
              ✓ 3/3 Donors Active
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() =>
              navigate("/v1/deep-analyze-3way", { state: cycle })
            }
            className="px-3 py-1.5 text-xs sm:text-sm font-semibold text-white transition duration-300 bg-purple-600 rounded-lg hover:bg-purple-700 shadow-sm flex items-center gap-1"
          >
            <i className="mdi mdi-stethoscope"></i>&nbsp; Deep Analyze
          </button>
          <button
            onClick={onToggle}
            className="p-1 text-gray-500 transition duration-300 rounded-full hover:bg-gray-200"
          >
            {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </button>
        </div>
      </div>

      {/* Cycle success score */}
      <div className="px-4 pb-2">
        <ThreeWayScores cycle={cycle} />
      </div>

      {/* 3 sub-pairs in a cycle flow */}
      <div className="p-4 pt-0">
        <div className="flex flex-col items-center justify-center gap-3 md:flex-row">
          <SubPairCard
            label="Pair 1"
            recipient={cycle.pair1.recipient}
            donor={cycle.pair2.donor}
            arrowLabel="← D2"
            bgColor="bg-blue-50"
          />
          <ArrowRightIcon />
          <SubPairCard
            label="Pair 2"
            recipient={cycle.pair2.recipient}
            donor={cycle.pair3.donor}
            arrowLabel="← D3"
            bgColor="bg-green-50"
          />
          <ArrowRightIcon />
          <SubPairCard
            label="Pair 3"
            recipient={cycle.pair3.recipient}
            donor={cycle.pair1.donor}
            arrowLabel="← D1"
            bgColor="bg-orange-50"
          />
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="p-4 pt-0 space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <SubPairDetails
              title="Pair 1: R1 ← D2"
              recipient={cycle.pair1.recipient}
              donor={cycle.pair2.donor}
              bgColor="bg-blue-100"
            />
            <SubPairDetails
              title="Pair 2: R2 ← D3"
              recipient={cycle.pair2.recipient}
              donor={cycle.pair3.donor}
              bgColor="bg-green-100"
            />
            <SubPairDetails
              title="Pair 3: R3 ← D1"
              recipient={cycle.pair3.recipient}
              donor={cycle.pair1.donor}
              bgColor="bg-orange-100"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const SubPairCard = ({ label, recipient, donor, arrowLabel, bgColor }) => {
  const getBloodGroup = (person) =>
    person?.blood_group?.label || person?.bloodgroup || "N/A";
  const getShortName = (person) => {
    if (person?.name) return person.name;
    const n =
      person?.donor_name || person?.recipient_name || person?.recipient_name || "";
    return n.length > 15 ? n.substring(0, 15) + "..." : n;
  };

  return (
    <div
      className={`${bgColor} rounded-lg p-3 shadow flex-1 w-full border border-gray-200`}
    >
      <p className="text-xs font-semibold text-gray-500 mb-1">{label}</p>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <FaUserInjured className="w-5 h-5 text-indigo-500" />
          <span className="text-sm font-medium text-gray-800">
            {getShortName(recipient)}
          </span>
        </div>
        <span className="text-xs font-bold text-purple-600">{arrowLabel}</span>
        <div className="flex items-center space-x-2">
          <FaHandHoldingHeart className="w-5 h-5 text-green-500" />
          <span className="text-sm font-medium text-gray-800">
            {getShortName(donor)}
          </span>
        </div>
      </div>
      <div className="flex justify-between mt-1 text-xs text-gray-500">
        <span>
          <FaTint className="inline mr-1 text-red-400" />
          {getBloodGroup(recipient)}
        </span>
        <span>
          <FaTint className="inline mr-1 text-red-400" />
          {getBloodGroup(donor)}
        </span>
      </div>
    </div>
  );
};

const SubPairDetails = ({ title, recipient, donor, bgColor }) => {
  const getBloodGroup = (person) =>
    person?.blood_group?.label || person?.bloodgroup || "N/A";
  const getFullName = (person) => {
    if (person?.name) return person.name;
    return `${person?.member_title || ""} ${
      person?.donor_name || person?.recipient_name || ""
    } ${person?.last_name || ""}`.trim();
  };
  const getCode = (person) =>
    person?.recipient_code || person?.donor_code || "N/A";

  return (
    <div className={`${bgColor} rounded-lg p-4 shadow-sm border border-gray-200`}>
      <h4 className="text-sm font-semibold text-gray-700 mb-3">{title}</h4>
      <div className="space-y-1 text-xs">
        <p>
          <strong>Recipient:</strong> {getFullName(recipient)} ({getCode(recipient)}){" "}
          <FaTint className="inline ml-1 text-red-400" /> {getBloodGroup(recipient)}
        </p>
        <p>
          <strong>Donor:</strong> {getFullName(donor)} ({getCode(donor)}){" "}
          <FaTint className="inline ml-1 text-red-400" /> {getBloodGroup(donor)}
        </p>
      </div>
    </div>
  );
};

const ArrowRightIcon = () => (
  <div className="flex items-center justify-center py-2 md:py-0">
    <svg
      className="w-6 h-6 text-purple-400"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 7l5 5m0 0l-5 5m5-5H6"
      />
    </svg>
  </div>
);

export default ThreeWayCycleDisplay;
